import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { invalidatePlatformPermissions, usePlatformPermissions } from '@/hooks/usePlatformPermissions';
import { logPlatformActivity } from '@/lib/platformActivity';
import { PLATFORM_PERMISSION_GROUPS } from '@/lib/platformPermissions';
import {
  listPlatformTeam,
  createPlatformStaff,
  updatePlatformStaff,
  setPlatformStaffStatus,
  resetPlatformStaffPassword,
  deletePlatformStaff,
  type PlatformTeamRow,
} from '@/lib/platformTeam';
import Modal from '@/pages/dashboard/core/components/Modal';

// ─────────────────────────────────────────────────────────────
// SuperAdmin — Équipe plateforme.
// Ajoute des travailleurs qui accèdent au back-office avec des
// autorisations précises (par page / par module).
// ─────────────────────────────────────────────────────────────

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

function randomPassword(): string {
  const chars = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let out = '';
  for (let i = 0; i < 10; i += 1) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

function groupKeys(groupKey: string): string[] {
  const g = PLATFORM_PERMISSION_GROUPS.find((x) => x.key === groupKey);
  return g ? g.permissions.map((p) => p.key) : [];
}

export default function SuperAdminTeamPage() {
  const { user } = useAuth();
  const platform = usePlatformPermissions();
  const [members, setMembers] = useState<PlatformTeamRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<PlatformTeamRow | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [roleLabel, setRoleLabel] = useState('Agent plateforme');
  const [perms, setPerms] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [pwdTarget, setPwdTarget] = useState<PlatformTeamRow | null>(null);
  const [newPwd, setNewPwd] = useState('');
  const [pwdError, setPwdError] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setMembers(await listPlatformTeam());
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Impossible de charger l\u2019équipe.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const active = members.filter((m) => m.status === 'active').length;
    return { total: members.length, active, suspended: members.length - active };
  }, [members]);

  const openCreate = () => {
    setEditing(null);
    setName(''); setEmail(''); setPhone('');
    setPassword(randomPassword());
    setRoleLabel('Agent plateforme');
    setPerms([]);
    setFormError('');
    setOpen(true);
  };

  const openEdit = (m: PlatformTeamRow) => {
    setEditing(m);
    setRoleLabel(m.role_label || 'Agent plateforme');
    setPerms([...m.permissions]);
    setFormError('');
    setOpen(true);
  };

  const togglePerm = (key: string) => {
    setPerms((prev) => (prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]));
  };

  const toggleGroup = (groupKey: string) => {
    const keys = groupKeys(groupKey);
    setPerms((prev) => {
      const allOn = keys.every((k) => prev.includes(k));
      return allOn ? prev.filter((p) => !keys.includes(p)) : Array.from(new Set([...prev, ...keys]));
    });
  };

  const save = async () => {
    if (perms.length === 0) { setFormError('Sélectionnez au moins une autorisation.'); return; }
    setSaving(true);
    setFormError('');
    try {
      if (editing) {
        await updatePlatformStaff(editing.id, { roleLabel, permissions: perms });
        invalidatePlatformPermissions(editing.user_id);
        void logPlatformActivity({
          actorId: user?.id, actorName: user?.name, actorRole: platform.roleLabel,
          action: 'team.update',
          description: `Accès modifiés pour ${editing.name} (${perms.length} autorisation${perms.length > 1 ? 's' : ''})`,
          targetType: 'platform_staff', targetId: editing.user_id,
        });
      } else {
        if (!name.trim()) { setFormError('Renseignez le nom du travailleur.'); setSaving(false); return; }
        if (!email.trim()) { setFormError("Renseignez l'email du travailleur."); setSaving(false); return; }
        if (password.trim().length < 6) { setFormError('Le mot de passe doit contenir au moins 6 caractères.'); setSaving(false); return; }
        const newId = await createPlatformStaff({
          name, email, telephone: phone, password,
          roleLabel, permissions: perms, createdBy: user?.id ?? null,
        });
        void logPlatformActivity({
          actorId: user?.id, actorName: user?.name, actorRole: platform.roleLabel,
          action: 'team.create', level: 'success',
          description: `Nouveau travailleur plateforme : ${name} (${email}) — ${perms.length} autorisation(s)`,
          targetType: 'platform_staff', targetId: newId,
        });
      }
      setOpen(false);
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (m: PlatformTeamRow) => {
    const suspending = m.status === 'active';
    setBusyId(m.id);
    try {
      await setPlatformStaffStatus(m.id, suspending ? 'suspended' : 'active');
      invalidatePlatformPermissions(m.user_id);
      void logPlatformActivity({
        actorId: user?.id, actorName: user?.name, actorRole: platform.roleLabel,
        action: suspending ? 'team.suspend' : 'team.activate',
        level: suspending ? 'warning' : 'success',
        description: `${suspending ? 'Suspension' : 'Réactivation'} de ${m.name}`,
        targetType: 'platform_staff', targetId: m.user_id,
      });
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const removeMember = async (m: PlatformTeamRow) => {
    setBusyId(m.id);
    try {
      await deletePlatformStaff(m.id);
      invalidatePlatformPermissions(m.user_id);
      void logPlatformActivity({
        actorId: user?.id, actorName: user?.name, actorRole: platform.roleLabel,
        action: 'team.remove', level: 'danger',
        description: `Retrait du travailleur plateforme ${m.name}`,
        targetType: 'platform_staff', targetId: m.user_id,
      });
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const savePassword = async () => {
    if (!pwdTarget) return;
    setPwdSaving(true);
    setPwdError('');
    try {
      await resetPlatformStaffPassword(pwdTarget.user_id, newPwd);
      void logPlatformActivity({
        actorId: user?.id, actorName: user?.name, actorRole: platform.roleLabel,
        action: 'team.password',
        description: `Mot de passe réinitialisé pour ${pwdTarget.name}`,
        targetType: 'platform_staff', targetId: pwdTarget.user_id,
      });
      setPwdTarget(null);
      setNewPwd('');
    } catch (e) {
      setPwdError(e instanceof Error ? e.message : 'Échec de la réinitialisation.');
    } finally {
      setPwdSaving(false);
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Équipe plateforme</h2>
          <p className="text-sm text-foreground-500 mt-1">
            Ajoutez des travailleurs qui gèrent la plateforme, avec des autorisations précises.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer self-start"
        >
          <i className="ri-user-add-line"></i>
          Ajouter un travailleur
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 md:gap-4 mb-6 max-w-xl">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="text-2xl font-bold font-heading text-foreground-950">{stats.total}</div>
          <div className="text-xs text-foreground-500 mt-0.5">Travailleurs</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="text-2xl font-bold font-heading text-accent-600">{stats.active}</div>
          <div className="text-xs text-foreground-500 mt-0.5">Actifs</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="text-2xl font-bold font-heading text-foreground-500">{stats.suspended}</div>
          <div className="text-xs text-foreground-500 mt-0.5">Suspendus</div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : loadError ? (
        <div className="flex flex-col items-center py-16 text-center">
          <p className="text-sm text-red-500 mb-3">{loadError}</p>
          <button onClick={load} className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm cursor-pointer">Réessayer</button>
        </div>
      ) : members.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-admin-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-600">Aucun travailleur plateforme pour le moment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {members.map((m) => {
            const inactive = m.status !== 'active';
            return (
              <div key={m.id} className={`bg-background-50 border border-background-200/70 rounded-lg p-5 ${inactive ? 'opacity-70' : ''}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {m.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-foreground-950 truncate">{m.name}</div>
                      <div className="text-[11px] text-foreground-400 truncate">
                        {m.user_name ? `@${m.user_name}` : ''}{m.email ? ` · ${m.email}` : ''}
                      </div>
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${inactive ? 'bg-foreground-100 text-foreground-600' : 'bg-accent-100 text-accent-900'}`}>
                    {inactive ? 'Suspendu' : 'Actif'}
                  </span>
                </div>

                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-900 font-medium">
                    <i className="ri-shield-user-line"></i>{m.role_label || 'Agent plateforme'}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 font-medium">
                    <i className="ri-key-2-line"></i>{m.permissions.includes('*') ? 'Accès complet' : `${m.permissions.length} autorisation${m.permissions.length > 1 ? 's' : ''}`}
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-3">
                  {PLATFORM_PERMISSION_GROUPS.filter((g) => g.permissions.some((p) => m.permissions.includes(p.key))).map((g) => (
                    <span key={g.key} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 font-medium">
                      <i className={g.icon}></i>{g.label}
                    </span>
                  ))}
                </div>

                <div className="flex items-center gap-2 pt-3 border-t border-background-200/60 flex-wrap">
                  <button
                    onClick={() => openEdit(m)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer"
                  >
                    <i className="ri-shield-user-line"></i> Gérer les accès
                  </button>
                  <button
                    onClick={() => { setPwdTarget(m); setNewPwd(randomPassword()); setPwdError(''); }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer"
                  >
                    <i className="ri-lock-password-line"></i> Mot de passe
                  </button>
                  <button
                    onClick={() => toggleStatus(m)}
                    disabled={busyId === m.id}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer disabled:opacity-50 ${inactive ? 'bg-accent-100 text-accent-900' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'}`}
                  >
                    <i className={inactive ? 'ri-user-follow-line' : 'ri-user-unfollow-line'}></i>
                    {inactive ? 'Réactiver' : 'Suspendre'}
                  </button>
                  <button
                    onClick={() => removeMember(m)}
                    disabled={busyId === m.id}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-500 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"
                  >
                    <i className="ri-delete-bin-line"></i> Retirer
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit modal */}
      {open && (
        <Modal
          title={editing ? 'Gérer les accès du travailleur' : 'Ajouter un travailleur plateforme'}
          onClose={() => setOpen(false)}
          maxWidth="max-w-2xl"
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button
                onClick={save}
                disabled={saving}
                className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                Enregistrer
              </button>
            </>
          }
        >
          {formError && <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{formError}</div>}

          <div className="space-y-4">
            {!editing && (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="block text-xs font-medium text-foreground-600 mb-1">Nom complet</span>
                    <input className={INPUT} value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex : Yassine Alami" />
                  </label>
                  <label className="block">
                    <span className="block text-xs font-medium text-foreground-600 mb-1">Téléphone (optionnel)</span>
                    <input className={INPUT} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Ex : +212 6 00 00 00 00" />
                  </label>
                </div>
                <label className="block">
                  <span className="block text-xs font-medium text-foreground-600 mb-1">Email (identifiant de connexion)</span>
                  <input type="email" className={INPUT} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Ex : yassine@zifek.fr" />
                </label>
                <div>
                  <span className="block text-xs font-medium text-foreground-600 mb-1">Mot de passe</span>
                  <div className="flex items-center gap-2">
                    <input className={INPUT} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="6 caractères minimum" />
                    <button
                      type="button"
                      onClick={() => setPassword(randomPassword())}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-background-100 text-foreground-700 text-xs font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer"
                    >
                      <i className="ri-refresh-line"></i> Générer
                    </button>
                  </div>
                  <p className="text-[11px] text-foreground-400 mt-1.5">Communiquez ces identifiants au travailleur : il se connecte avec cet email et ce mot de passe.</p>
                </div>
              </div>
            )}

            <div>
              <span className="block text-xs font-medium text-foreground-600 mb-1">Intitulé du poste</span>
              <input className={INPUT} value={roleLabel} onChange={(e) => setRoleLabel(e.target.value)} placeholder="Ex : Agent support, Modérateur thèmes..." />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-foreground-600">Autorisations</span>
                <span className="text-[11px] text-foreground-400">{perms.length} sélectionnée(s)</span>
              </div>
              <div className="space-y-2 max-h-[44vh] overflow-y-auto pr-1">
                {PLATFORM_PERMISSION_GROUPS.map((g) => {
                  const keys = g.permissions.map((p) => p.key);
                  const allOn = keys.every((k) => perms.includes(k));
                  const someOn = keys.some((k) => perms.includes(k));
                  return (
                    <div key={g.key} className={`rounded-lg border p-3 transition-colors ${someOn ? 'border-primary-200 bg-primary-50/40' : 'border-background-200/70'}`}>
                      <button type="button" onClick={() => toggleGroup(g.key)} className="w-full flex items-center gap-3 text-left cursor-pointer">
                        <span className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 ${allOn ? 'bg-primary-500 border-primary-500 text-background-50' : someOn ? 'bg-primary-100 border-primary-300 text-primary-700' : 'border-background-300/60 text-transparent'}`}>
                          <i className={allOn ? 'ri-check-line text-xs' : someOn ? 'ri-subtract-line text-xs' : 'ri-check-line text-xs'}></i>
                        </span>
                        <span className="w-8 h-8 rounded-lg bg-background-100 text-foreground-600 flex items-center justify-center flex-shrink-0">
                          <i className={g.icon}></i>
                        </span>
                        <span className="text-sm font-semibold text-foreground-800 flex-1 min-w-0 truncate">{g.label}</span>
                      </button>
                      <div className="mt-2 ml-8 flex flex-wrap gap-x-4 gap-y-1.5">
                        {g.permissions.map((p) => (
                          <label key={p.key} className="flex items-center gap-2 text-xs text-foreground-600 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={perms.includes(p.key)}
                              onChange={() => togglePerm(p.key)}
                              className="w-3.5 h-3.5 rounded border-background-300/60 accent-primary-500 cursor-pointer"
                            />
                            {p.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Reset password modal */}
      {pwdTarget && (
        <Modal
          title="Réinitialiser le mot de passe"
          onClose={() => setPwdTarget(null)}
          maxWidth="max-w-md"
          footer={
            <>
              <button onClick={() => setPwdTarget(null)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button
                onClick={savePassword}
                disabled={pwdSaving}
                className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2"
              >
                {pwdSaving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                Enregistrer
              </button>
            </>
          }
        >
          <p className="text-xs text-foreground-500 mb-3">
            Nouveau mot de passe pour <strong className="text-foreground-800">{pwdTarget.name}</strong> ({pwdTarget.email}).
          </p>
          {pwdError && <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{pwdError}</div>}
          <div className="flex items-center gap-2">
            <input className={INPUT} value={newPwd} onChange={(e) => setNewPwd(e.target.value)} placeholder="6 caractères minimum" />
            <button
              type="button"
              onClick={() => setNewPwd(randomPassword())}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-background-100 text-foreground-700 text-xs font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line"></i> Générer
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}